<div class="ai-search-dialog">
  <!-- Type Selection Step -->
  <ng-container *ngIf="step === 'select-type'">
    <div mat-dialog-title>
      <h2>Start your search</h2>
      <p class="subtitle" *ngIf="searchTerm">
        What kind of entity is <strong>{{ searchTerm }}</strong
        >?
      </p>
      <p class="subtitle" *ngIf="!searchTerm">
        What kind of entity are you looking for?
      </p>
    </div>

    <div mat-dialog-content>
      <div class="entity-type-cards">
        <div
          class="entity-type-card"
          id="ai-search-company"
          [class.selected]="entityType === 'company'"
          (click)="selectType('company')"
        >
          <span class="card-title">Company</span>
          <span class="card-subtitle"
            >Public, private or government entity</span
          >
          <span class="card-example"
            >e.g. Volkswagen, <br />
            Amazon, Apple</span
          >
        </div>
        <div
          class="entity-type-card"
          id="ai-search-topic"
          [class.selected]="entityType === 'topic'"
          (click)="selectType('topic')"
        >
          <span class="card-title">Topic</span>
          <span class="card-subtitle"
            >Theme, event or <br />issue to track</span
          >
          <span class="card-example">e.g. ESG, Inflation, <br />M&A</span>
        </div>
        <div
          class="entity-type-card"
          id="ai-search-commodity"
          [class.selected]="entityType === 'commodity'"
          (click)="selectType('commodity')"
        >
          <span class="card-title">Commodity</span>
          <span class="card-subtitle"
            >Raw material or <br />
            traded asset</span
          >
          <span class="card-example">e.g. Gold, Oil, Gas</span>
        </div>
      </div>
    </div>

    <div mat-dialog-actions>
      <button mat-raised-button color="secondary" mat-dialog-close>
        Cancel
      </button>
      <button mat-raised-button color="primary" (click)="continueToForm()">
        Continue
      </button>
    </div>
  </ng-container>

  <!-- Configure Step -->
  <ng-container *ngIf="step === 'configure'">
    <!-- Kept in the DOM (hidden, not destroyed) during enrichment: the
         company form owns the in-flight enrichment subscription. -->
    <div [class.ai-loading-hidden]="companyEnriching">
      <div mat-dialog-title>
        <h2 *ngIf="entityType !== 'company'">Configure Your Search</h2>
        <h2 *ngIf="entityType === 'company'">Configure Your Query</h2>
        <p class="subtitle" *ngIf="entityType === 'company'">
          Enter a company name to auto-generate search parameters
        </p>
        <p class="subtitle" *ngIf="entityType === 'topic'">
          Enter topic details to automatically generate tailored search
          parameters
        </p>
        <p class="subtitle" *ngIf="entityType === 'commodity'">
          Enter commodity details to automatically generate tailored search
          parameters
        </p>
      </div>

      <div mat-dialog-content>
        <!-- Company Form -->
        <app-company-form
          *ngIf="entityType === 'company'"
          panelClass="setup-overlay-panel"
          [initialLegalName]="searchTerm"
          [autoSetAsNewCompany]="!!searchTerm"
          [userEmail]="userEmail"
          [project]="project"
          (submitForm)="onCompanySubmit($event)"
          (navigateToEntity)="onNavigateToEntity($event)"
          (searchStateChange)="onSearchStateChange($event)"
        ></app-company-form>

        <!-- Topic Form -->
        <ng-container *ngIf="entityType === 'topic'">
          <div class="form-row">
            <mat-form-field appearance="outline">
              <mat-label>Topic</mat-label>
              <input
                matInput
                type="text"
                [(ngModel)]="topic"
                placeholder="e.g. ESG, inflation, M&A"
                required
              />
            </mat-form-field>
          </div>
          <div class="form-row">
            <mat-form-field appearance="outline">
              <mat-label>Company (optional)</mat-label>
              <input
                matInput
                type="text"
                [formControl]="entityControl"
                [matAutocomplete]="autoEntity"
                placeholder="Anchor the topic to one entity"
                (blur)="onEntityBlur(autoEntity.isOpen)"
              />
              <mat-autocomplete
                #autoEntity="matAutocomplete"
                [class]="'ai-picker-panel setup-overlay-panel'"
                [displayWith]="displayEntity"
                (optionSelected)="selectEntity($event.option.value)"
                (closed)="onEntityBlur()"
              >
                <ng-container *ngIf="entityResults$ | async as hits">
                  <mat-optgroup *ngIf="hits.length">
                    <div class="search-header">
                      <span class="header-name">Entity</span>
                      <span class="header-country">Country</span>
                      <span class="header-count"># Articles</span>
                    </div>
                    <mat-option *ngFor="let hit of hits" [value]="hit">
                      <div class="search-result-row">
                        <span class="result-name">
                          {{ hit.entity.name }}
                          <!-- The field asks for a company; name the type only
                               when a hit is something else. -->
                          <span
                            class="result-meta"
                            *ngIf="hit.entity.type !== 'company'"
                            >{{ hit.entity.type }}</span
                          >
                        </span>
                        <span class="result-country">{{
                          hit.entity.country?.alpha_id | uppercase
                        }}</span>
                        <span class="result-count">{{ hit.count ?? '-' }}</span>
                      </div>
                    </mat-option>
                  </mat-optgroup>
                </ng-container>
              </mat-autocomplete>
            </mat-form-field>
          </div>
        </ng-container>

        <!-- Commodity Form -->
        <ng-container *ngIf="entityType === 'commodity'">
          <div class="form-row">
            <mat-form-field appearance="outline">
              <mat-label>Commodity</mat-label>
              <input
                matInput
                type="text"
                [(ngModel)]="commodity"
                placeholder="e.g. Gold, Oil, Gas"
                required
              />
            </mat-form-field>
          </div>
        </ng-container>

        <div class="error-message" *ngIf="errorMessage">
          {{ errorMessage }}
        </div>
      </div>

      <div mat-dialog-actions>
        <button mat-raised-button color="secondary" (click)="goBack()">
          Back
        </button>

        <!-- Company: Search button (enrichment + generation run automatically) -->
        <button
          *ngIf="entityType === 'company'"
          mat-raised-button
          color="primary"
          [disabled]="!companySearchEnabled"
          (click)="triggerCompanySearch()"
        >
          <ng-container *ngIf="!companyEnriching">Search</ng-container>
          <ng-container *ngIf="companyEnriching">
            <mat-spinner [diameter]="18" class="inline-spinner"></mat-spinner>
            Searching...
          </ng-container>
        </button>

        <!-- Topic / Commodity -->
        <button
          *ngIf="entityType !== 'company'"
          mat-raised-button
          color="primary"
          [disabled]="!isFormValid"
          (click)="generateQuery()"
        >
          Generate AI Search
        </button>
      </div>
    </div>

    <app-ai-loading-card
      *ngIf="companyEnriching"
      phase="enriching"
      [subject]="loadingSubject"
      [showStages]="entityType === 'company'"
      (cancelLoading)="cancelLoading()"
    ></app-ai-loading-card>
  </ng-container>

  <!-- Loading Step -->
  <app-ai-loading-card
    *ngIf="step === 'loading'"
    phase="generating"
    [subject]="loadingSubject"
    [showStages]="entityType === 'company'"
    (cancelLoading)="cancelLoading()"
  ></app-ai-loading-card>
</div>
